import { Box, DialogActions, DialogContent, styled } from '@mui/material';
import FlexBox from 'Components/FlexBox';
import Loader from 'Components/Forms/Loader';
import { DialogDefault, ThemeButton, ThemeTypography } from 'DesignSystem';
import GeoDataItem from 'DesignSystem/DataDisplay/GeoDataItem/GeoDataItem';
import useDatasetMutation from 'FeatureModules/DataImport/hooks/useDatasetMutation';
import {
  geoDataCustomValidation,
  geoMappingDatasetSchema,
  geoUploadDatasetSchema,
  IGeoMappingFormValues,
  IGeoUploadFormValues,
  mappedColumnsSchema,
} from 'FeatureModules/DataImport/schemas/geoUpload.schema';
import {
  EDatasetErrorEntity,
  EGeoDataStatus,
  IDataset,
} from 'FeatureModules/DataImport/types/dataset.types';
import {
  CSV_SEPARATOR_OPTIONS,
  DECIMAL_SEPARATOR_OPTIONS,
} from 'FeatureModules/DataImport/utils/dataImport.utils';
import useUploadState from 'FeatureModules/Document/hooks/useUploadState';
import { IBasePartnerCompany } from 'FeatureModules/Partner/types/partner.types';
import { Form, Formik, FormikProps } from 'formik';
import { IPreProcessDatasetInput } from 'graphql/secure/mutations/types/dataset-mutation.types';
import useDialog from 'hooks/useDialog';
import useMessages from 'hooks/useMessages';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IDefaultDialogProps } from 'types/dialog.types';
import DatasetMappingForm from '../Forms/DatasetMappingForm';
import DatasetUploadForm from '../Forms/DatasetUploadForm';

interface IDatasetImportDialogProps extends IDefaultDialogProps {
  /**
   * The partner company that the dataset will be owned by
   * Passing this will disable the owner selection in the upload form
   */
  ownedBy?: IBasePartnerCompany;
  /**
   * The ID of the cluster to which the dataset should add the sites
   */
  defaultClusterId?: string;
}

const InfoContainer = styled(FlexBox)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'normal',
  padding: theme.spacing(2),
  borderRadius: '6px',
  border: `1px solid ${theme.custom.themeColors.primary[40]}`,
  background: theme.custom.themeColors.primary[5],
  '& ul': {
    paddingInlineStart: theme.spacing(2),
    marginTop: theme.spacing(1),
    marginBottom: 0,
  },
}));

const DatasetImportDialog: FC<IDatasetImportDialogProps> = ({
  open,
  ownedBy,
  defaultClusterId,
  onClose,
}) => {
  const [dataset, setDataset] = useState<IDataset | undefined>(undefined);

  const { createDataset, updateDataset, deleteDataset, startDatasetPreProcessing } =
    useDatasetMutation();

  const uploadFormRef = useRef<FormikProps<IGeoUploadFormValues>>(null);
  const mappingFormRef = useRef<FormikProps<IGeoMappingFormValues>>(null);
  const { openDialog } = useDialog();
  const { undoFileUploads, handleCleanCreatedRecords } = useUploadState();
  const navigate = useNavigate();
  const location = useLocation();
  const { setErrorMessage, setSuccessMessage } = useMessages();

  const uploadInitialFormValues: IGeoUploadFormValues = {
    ...geoUploadDatasetSchema.default(),
    ...(ownedBy ? { ownedBy } : {}),
  };

  useEffect(() => {
    if (mappingFormRef.current && dataset) {
      const initialValues: IGeoMappingFormValues = {
        ...geoMappingDatasetSchema.default(),
        datasetId: dataset.id,
        mappedColumns:
          dataset.mappedColumns?.map(column => {
            return {
              ...mappedColumnsSchema.default(),
              sourceColumnName: column.columnName,
              columnDataExample: column.columnDataExample,
              ...(column.mappedColumnName ? { targetColumnName: column.mappedColumnName } : {}),
            };
          }) || [],
      };

      mappingFormRef.current.setValues(initialValues, false);
    }
  }, [dataset, mappingFormRef]);

  const handleDatasetCreated = (createdDataset: IDataset) => {
    if (createdDataset.status === EGeoDataStatus.VALIDATED) {
      setSuccessMessage(
        `Farm site(s) have been created or updated and are now also available in your Partners and Sites library.`
      );
    }
    navigate(
      `${location.pathname}/datasets/${createdDataset.id}${
        defaultClusterId ? `?clusterId=${defaultClusterId}` : ''
      }`
    );
    onClose?.();
  };

  const handleSubmitUpload = async (values: IGeoUploadFormValues) => {
    try {
      if (!values) {
        throw new Error('The form values are not defined');
      }

      let datasetId = values.datasetId;

      // Create the dataset if it d not exists yet
      if (!datasetId) {
        const datasetCreationResults = await createDataset(values);
        datasetId = datasetCreationResults.data?.createDataset.dataset.id;
        // Update form with the created dataset ids
        uploadFormRef.current?.setValues({ ...values, datasetId }, false);
      } else {
        // Update the datasets that already exists
        await updateDataset({ ...values, datasetId });
      }

      if (!datasetId) {
        throw new Error('An issue occurred while creating the datasets');
      }

      const input: IPreProcessDatasetInput = {
        datasetId,
        autoCloseGeojsonGeometries: values.fileConfiguration?.autoCloseGeojsonGeometries,
        decimalSeperator:
          (values.fileConfiguration?.decimalSeparator &&
            DECIMAL_SEPARATOR_OPTIONS[values.fileConfiguration.decimalSeparator].value) ||
          undefined,
        csvSeperator:
          (values.fileConfiguration?.csvSeparator &&
            CSV_SEPARATOR_OPTIONS[values.fileConfiguration.csvSeparator].value) ||
          undefined,
      };
      const response = await startDatasetPreProcessing(input);

      if (!response.data?.preProcessDataset?.dataset) {
        throw new Error('Datasets was not created');
      }

      const datasetProcessingError = response.data?.preProcessDataset.datasetProcessingError;

      if (!datasetProcessingError && response.errors?.length) {
        throw new Error('Unidentified errors occurred in the backend');
        // If some files could not be processed, we can not continue
      } else if (
        response.data?.preProcessDataset.dataset.status === EGeoDataStatus.PARSING_FAILED
      ) {
        // If there are any parsing errors in the dataset we try to show them on the correct file in the upload form
        if (
          datasetProcessingError?.datasetErrors.some(
            ({ entityType }) => entityType === EDatasetErrorEntity.DOCUMENT
          )
        ) {
          // We need to place the error at the correct array index of the form
          // 1. Find the index of the datasetId in the form values matching the processingError
          // 2. Find the index of the file in the files array matching the processingError
          uploadFormRef.current?.setValues(prev => {
            if (datasetProcessingError) {
              return {
                ...prev,
                files: prev.files.map(file => {
                  const fileProcessingError = datasetProcessingError.datasetErrors.find(
                    ({ entityId }) => entityId === file.id
                  );
                  if (!fileProcessingError) {
                    return file;
                  }
                  return {
                    ...file,
                    processingError: {
                      message: fileProcessingError.errorMessage,
                      type: fileProcessingError.errorType,
                    },
                  };
                }),
              };
            }
            return prev;
          });
        } else if (
          !!datasetProcessingError &&
          datasetProcessingError.datasetErrors.some(
            ({ entityType }) => entityType === EDatasetErrorEntity.DATASET
          )
        ) {
          datasetProcessingError.datasetErrors.forEach(({ errorMessage }) => {
            setErrorMessage(`The following processing issue occurred: ${errorMessage}`);
          });
        } else {
          throw new Error(
            'The dataset errors occurred while parsing the dataset could not be matched to the files in the form'
          );
        }
        // If there are mapping errors we show the mapping form
      } else if (
        response.data?.preProcessDataset.dataset.status === EGeoDataStatus.MAPPING_FAILED ||
        response.data?.preProcessDataset.dataset.status === EGeoDataStatus.MAPPED
      ) {
        setDataset(response.data.preProcessDataset?.dataset);
        // If there are no errors the dataset got created and we show the it in the dataset overview
      } else if (response.data?.preProcessDataset?.dataset) {
        handleDatasetCreated(response.data.preProcessDataset.dataset);
      } else {
        throw new Error(
          'Unknown error occurred submitting the mapping and processing. No dataset was created.'
        );
      }
    } catch (error) {
      console.error(error);
      setErrorMessage(
        'At least one error occurred while processing the dataset. If the error persists, please contact support.'
      );
    } finally {
      uploadFormRef.current?.setSubmitting(false);
    }
  };

  const handleSubmitMapping = async (values: IGeoMappingFormValues) => {
    try {
      const response = await startDatasetPreProcessing({
        datasetId: values.datasetId,
        overwriteMappedColumns: true,
        // Remove the excluded columns and the columnDataExample
        mappedColumns:
          values.mappedColumns
            .filter(({ removed }) => !removed)
            .map(mapped => omit(mapped, ['columnDataExample', 'removed'])) || [],
      });

      // Unknown errors
      if (!response.data?.preProcessDataset?.dataset) {
        throw new Error('No datasets were created');
      } else if (
        response.data?.preProcessDataset.dataset.status === EGeoDataStatus.MAPPING_FAILED ||
        response.data?.preProcessDataset.dataset.status === EGeoDataStatus.MAPPED
      ) {
        // Throw error from backend to the user
        if (response.data?.preProcessDataset.datasetProcessingError?.datasetErrors.length) {
          setErrorMessage(
            response.data.preProcessDataset.datasetProcessingError.datasetErrors[0].errorMessage
          );
          return;
          // Dataset mapping failed
        } else {
          throw new Error('Dataset mapping failed.');
        }
      }

      if (response.data?.preProcessDataset?.dataset) {
        handleDatasetCreated(response.data.preProcessDataset.dataset);
      } else {
        throw new Error(
          'Unknown error occurred submitting the mapping and processing. No dataset was created.'
        );
      }
    } catch (error) {
      console.error(error);
      setErrorMessage(
        'At least one error occurred while processing the dataset. If the error persists, please contact support.'
      );
    } finally {
      mappingFormRef.current?.setSubmitting(false);
    }
  };

  const undoChangesAndClose = () => {
    onClose?.();
    // If dataset got created delete it (can be in the mapping or upload state with processing errors)
    const datasetIdToDelete = uploadFormRef.current?.values?.datasetId || dataset?.id;
    if (datasetIdToDelete) {
      deleteDataset({ id: datasetIdToDelete });
    }
    undoFileUploads();
    handleCleanCreatedRecords();
  };

  // If there are unsaved changes, ask the user if they want to leave the page
  const handleClose = () => {
    if (
      (uploadFormRef.current &&
        !isEqual(uploadFormRef.current.values, uploadFormRef.current.initialValues)) ||
      (mappingFormRef.current && dataset)
    ) {
      openDialog({
        type: 'ALERT',
        props: {
          title: 'Unsaved changes',
          text: 'Are you sure you want to close this window? All unsaved changes will be lost and you will not be able to undo this action.',
          submitText: 'Discard changes',
          displayCloseButton: true,
          onSubmit: () => {
            undoChangesAndClose();
          },
          onCancel: () => undefined,
        },
      });
    } else {
      undoChangesAndClose();
    }
  };

  return (
    <DialogDefault
      open={!!open}
      title={
        dataset?.status === EGeoDataStatus.MAPPING_FAILED ||
        dataset?.status === EGeoDataStatus.MAPPED
          ? 'Map uploaded columns'
          : 'Upload new dataset'
      }
      iconName="upload"
      maxWidth="xl"
      fullWidth
      onClose={handleClose}
    >
      <>
        {/* Upload & dataset creation */}
        {!dataset && (
          <Formik<IGeoUploadFormValues>
            innerRef={uploadFormRef}
            initialValues={uploadInitialFormValues}
            validateOnChange
            initialStatus={{ uploadCompleted: true }}
            validationSchema={geoUploadDatasetSchema}
            onSubmit={handleSubmitUpload}
          >
            {({ isSubmitting, isValid, status }) => (
              <Form>
                <DialogContent>
                  <Box maxHeight="75vh" overflow="auto">
                    <DatasetUploadForm disableOwnerSelection={!!ownedBy} />
                  </Box>
                </DialogContent>

                <DialogActions sx={{ justifyContent: 'space-between' }}>
                  <ThemeButton color="BLUE_ICE" onClick={handleClose}>
                    Cancel
                  </ThemeButton>
                  <ThemeButton
                    color="PRIMARY"
                    type="submit"
                    loading={isSubmitting}
                    disabled={!isValid || !status?.uploadCompleted}
                  >
                    Continue
                  </ThemeButton>
                </DialogActions>
              </Form>
            )}
          </Formik>
        )}

        {/* Dataset column mapping */}
        {(dataset?.status === EGeoDataStatus.MAPPING_FAILED ||
          dataset?.status === EGeoDataStatus.MAPPED) && (
          <Formik<IGeoMappingFormValues>
            innerRef={mappingFormRef}
            initialValues={geoMappingDatasetSchema.default()}
            validationSchema={geoMappingDatasetSchema}
            validate={geoDataCustomValidation}
            onSubmit={handleSubmitMapping}
          >
            {({ isSubmitting, isValid, values, errors }) => (
              <Form>
                <DialogContent sx={{ maxHeight: '75vh' }}>
                  <Box display="flex" flexDirection="column" mb={2} gap={2}>
                    <FlexBox width="100%" gap={2}>
                      <ThemeTypography width="50%" variant="BODY_MEDIUM">
                        The columns of your uploaded dataset are listed here. Please link the data
                        of your uploaded file with the correct template data type in the system by
                        selecting a template column for each uploaded column. If there is no
                        matching template column selection for an uploaded column, this data point
                        is currently not available for import and needs to be excluded. Click the
                        minus/plus icon to exclude or include a data point. The dataset can only be
                        saved once all columns are mapped or excluded.
                      </ThemeTypography>

                      <InfoContainer width="50%">
                        <ThemeTypography variant="BODY_MEDIUM_BOLD">
                          Required template columns to complete the import of farm sites with
                          geometry data:
                        </ThemeTypography>
                        <ul>
                          <li>
                            <ThemeTypography variant="BODY_MEDIUM">
                              Plot geometry (WKT format or point coordinates).
                            </ThemeTypography>
                          </li>
                          <li>
                            <ThemeTypography variant="BODY_MEDIUM">
                              Plot ID or Farm ID (Preferably both)
                            </ThemeTypography>
                          </li>
                        </ul>
                      </InfoContainer>
                    </FlexBox>

                    <InfoContainer>
                      <ThemeTypography variant="BODY_MEDIUM">
                        If you already group your farms based on some id (e.g. community id,
                        cooperative id), you can map this column to Origin cluster ID, which will
                        save those farms in clusters accordingly.
                      </ThemeTypography>
                    </InfoContainer>
                  </Box>

                  <GeoDataItem
                    active
                    title={dataset.title}
                    commodity={dataset.rawMaterial.title}
                    countryCode={dataset.originCountry}
                    owner={dataset.ownedBy.name}
                    status={errors?.mappedColumns?.length ? 'error' : undefined}
                  />

                  {!mappedColumnsSchema || !values ? (
                    <Loader />
                  ) : (
                    <Box overflow="auto">
                      <DatasetMappingForm />
                    </Box>
                  )}
                </DialogContent>

                <DialogActions sx={{ justifyContent: 'space-between' }}>
                  <ThemeButton color="BLUE_ICE" onClick={handleClose}>
                    Cancel
                  </ThemeButton>
                  <ThemeButton
                    color="PRIMARY"
                    type="submit"
                    loading={isSubmitting}
                    disabled={!isValid}
                  >
                    Save dataset
                  </ThemeButton>
                </DialogActions>
              </Form>
            )}
          </Formik>
        )}
      </>
    </DialogDefault>
  );
};

export default DatasetImportDialog;
